import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { parse } from 'qs';
import { Badge, Alert, Skeleton } from 'antd';
import {
  GiftFilled,
} from '@ant-design/icons';
import {
  CloudArrowUp,
  PaperPlaneTilt,
  EnvelopeSimple,
  ListChecks,
  Users,
  UserSwitch,
  ChartLine,
  AddressBook,
  GearSix,
  Phone,
  Question,
  Gift,
  CaretRight,
  Sparkle,
  ChatsCircle,
} from '@phosphor-icons/react';
import axios from 'axios';
import posthog from 'posthog-js';
import { openModal } from '../actions/modal';
import { setAiChatVisible } from '../actions/aiChat';
import { getNotifications } from '../actions/notifications';
import { captureAnalytics } from '../actions/preload';
import { getLoginInfo, resetPatientAccessAttempt } from '../actions/auth';
import { getUserInfo, getActiveFeatures } from '../actions/userProfile';
import { changeDefaultOrganization } from '../actions/appActions';
import {
  verifyFeatureAccess,
  verifyReceivedInboxAccess,
  verifySentInboxAccess,
  verifyPatientRecaptureAccess,
  verifyPatientEngagementAccess,
  hasFeatureInLocalStorage,
  USER_ACTIVITY_ANALYTICS_FEATURE,
} from '../global/featureFlags';
import isOnChrome from '../global/browser';
import {
  shouldShowMacPrinterDriverAnnouncement,
  markMacPrinterDriverAnnouncementShown,
} from '../util/macPrinterDriverAnnouncement';
import { AI_ON_UPLOADS_FEATURES, mayShowAiOnUploadsPrompt, orgHasAiOnUploads } from '../util/aiOnUploadsPrompt';

import {
  chromeExtensionNotInstalled,
  installChromeExtension,
} from '../assets/copy/homePage';
import Intro from './NewHome/Intro';
import Banner from '../components/Banner';
import UpsellFlash from '../components/UpsellFlash';
import COLORS from '../util/constants/colors';
import { parseFullName } from 'parse-full-name';
import './home.scss';

const DashboardCard = ({ icon, text, description, colorClass, badgeText, count }) => (
  <Badge count={badgeText || count}>
    <div className="dashboard-card">
      <div className={`dashboard-card-icon ${colorClass || 'dashboard-card-icon--purple'}`}>
        {icon}
      </div>
      <div className="dashboard-card-content">
        <div className="dashboard-card-title">{text}</div>
        {description && <div className="dashboard-card-description">{description}</div>}
      </div>
      <CaretRight size={16} className="dashboard-card-arrow" />
    </div>
  </Badge>
);

DashboardCard.defaultProps = {
  count: 0,
  badgeText: null,
  description: null,
  colorClass: null,
};

DashboardCard.propTypes = {
  text: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  description: PropTypes.string,
  colorClass: PropTypes.string,
  count: PropTypes.number,
  badgeText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};

const DashboardButton = (props) => {
  const { to, onClick, external, ...cardProps } = props;
  if (onClick && !to) {
    return (
      <div onClick={onClick} style={{ cursor: 'pointer' }}>
        <DashboardCard {...cardProps} />
      </div>
    );
  }
  if (external) {
    return (
      <a href={to} onClick={onClick} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
        <DashboardCard {...cardProps} />
      </a>
    );
  }
  return (
    <Link to={to} onClick={onClick} style={{ textDecoration: 'none' }}>
      <DashboardCard {...cardProps} />
    </Link>
  );
};

DashboardButton.defaultProps = {
  external: false,
  onClick: null,
  to: null,
};

DashboardButton.propTypes = {
  to: PropTypes.string,
  external: PropTypes.bool,
  onClick: PropTypes.func,
};

class Home extends Component {
  constructor(props) {
    super();
    const { organizationDetails: { current_organization: currentOrganization } } = props;
    this.state = {
      userEMR: localStorage.getItem('m8_emr') || props.emr,
      openOrganizationFilterMenu: false,
      currentOrganization,
      chromeExtensionInstalled: true,
      canViewVoiceAi: false,
      hasUserActivity: false,
      canAccessAiChat: false,
      canAccessReceivedInbox: true,
      canAccessSentInbox: true,
      productUpdates: [],
      productUpdatesLoading: true,
      canAccessPatientRecapture: false,
      canAccessPatientEngagement: false,
    };
    this.carouselRef = React.createRef();
    this.updatesRef = React.createRef();
    this.isDragging = false;
    this.dragStartX = 0;
    this.scrollStartX = 0;
    this.hasDragged = false;
    this.scrollAnimationId = null;
    this.hoverDirection = null;
  }

  handleCarouselMouseDown = (e) => {
    const el = this.carouselRef.current;
    if (!el) return;
    e.preventDefault();
    this.stopHoverScroll();
    this.isDragging = true;
    this.hasDragged = false;
    this.dragStartX = e.pageX;
    this.scrollStartX = el.scrollLeft;
    el.style.scrollBehavior = 'auto';
    el.classList.add('dashboard-updates-carousel--dragging');
  }

  handleCarouselMouseMove = (e) => {
    if (!this.isDragging) {
      this.handleCarouselHoverMove(e);
      return;
    }
    const el = this.carouselRef.current;
    if (!el) return;
    e.preventDefault();
    const dx = e.pageX - this.dragStartX;
    if (Math.abs(dx) > 3) this.hasDragged = true;
    el.scrollLeft = this.scrollStartX - dx;
  }

  handleCarouselMouseUp = () => {
    if (!this.isDragging) return;
    this.isDragging = false;
    const el = this.carouselRef.current;
    if (el) {
      el.style.scrollBehavior = '';
      el.classList.remove('dashboard-updates-carousel--dragging');
    }
  }

  handleCarouselMouseLeave = (e) => {
    this.handleCarouselMouseUp(e);
    this.stopHoverScroll();
  }

  handleCarouselClick = (e) => {
    if (this.hasDragged) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  startHoverScroll = (direction) => {
    if (this.hoverDirection === direction && this.scrollAnimationId) return;
    this.stopHoverScroll();
    this.hoverDirection = direction;
    const el = this.carouselRef.current;
    if (!el) return;
    el.style.scrollBehavior = 'auto';
    const step = direction === 'left' ? -4 : 4;
    const scroll = () => {
      const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
      if (direction === 'left' && el.scrollLeft <= 0) {
        this.stopHoverScroll();
        return;
      }
      if (direction === 'right' && el.scrollLeft >= maxScroll - 1) {
        this.stopHoverScroll();
        return;
      }
      el.scrollLeft += step;
      this.scrollAnimationId = requestAnimationFrame(scroll);
    };
    this.scrollAnimationId = requestAnimationFrame(scroll);
  }

  stopHoverScroll = () => {
    if (this.scrollAnimationId) {
      cancelAnimationFrame(this.scrollAnimationId);
      this.scrollAnimationId = null;
    }
    this.hoverDirection = null;
    const el = this.carouselRef.current;
    if (el) el.style.scrollBehavior = '';
  }

  handleCarouselHoverMove = (e) => {
    const el = this.carouselRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const edgeSize = 60;
    const fromLeft = e.clientX - rect.left;
    const fromRight = rect.right - e.clientX;
    if (fromLeft <= edgeSize) {
      this.startHoverScroll('left');
      return;
    }
    if (fromRight <= edgeSize) {
      this.startHoverScroll('right');
      return;
    }
    this.stopHoverScroll();
  }

  handleCarouselScroll = () => {
    const el = this.carouselRef.current;
    const wrapper = this.updatesRef.current;
    if (!el || !wrapper) return;
    const atStart = el.scrollLeft <= 0;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
    wrapper.classList.toggle('dashboard-updates--scrolled-left', !atStart);
    wrapper.classList.toggle('dashboard-updates--scrollable-right', !atEnd);
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.productUpdates !== this.state.productUpdates) {
      this.handleCarouselScroll();
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
    this.stopHoverScroll();
    window.removeEventListener('resize', this.handleCarouselScroll);
  }

  checkChromeExtensionInstallation() {
    const ping = { message: 'ping' };
    const chromeExtId = 'onpcajmldffklnfccjolcccoijaogggl';

    return new Promise((resolve, reject) => {
      /* global chrome */
      chrome.runtime.sendMessage(chromeExtId, ping, (response) => {
        if (response && response.message) return resolve();
        return reject(chrome.runtime.lastError.message || 'Error');
      });
    });
  }

  showChromeExtensionInformation() {
    this.checkChromeExtensionInstallation()
      .then(() => {
        this.setState({ chromeExtensionInstalled: true });
      })
      .catch((error) => {
        this.setState({ chromeExtensionInstalled: false });
      });
  }

  componentWillMount() {
    if (this.props.isAuthenticated && this.props.organizationDetails) {
      this.props.getNotifications();
    }
    if (this.props.hasCompletedWalkthrough || this.props.impersonating) return;
    this.props.history.replace('/app/walkthrough');
  }

  // ENG-3664: one landing-page modal at a time, so this waits behind the
  // announcements above and re-checks the open modal after its own fetch.
  maybeShowAiOnUploadsPrompt = async () => {
    const {
      isAdmin, hasCompletedWalkthrough, impersonating, user,
    } = this.props;
    const uid = user?.uid;
    if (!hasCompletedWalkthrough || impersonating || !mayShowAiOnUploadsPrompt({ isAdmin, uid })) return;
    if (this.props.modalType.type) return;

    try {
      // The sign-in flags cache already knows about orgs that opted in before
      // this session, so they never pay for the fetch below.
      const cached = await Promise.all(AI_ON_UPLOADS_FEATURES.map((name) => verifyFeatureAccess(name)));
      if (cached.some(Boolean)) return;

      // Read the response, not the store: a failed fetch leaves the store's
      // feature list as it was, which must not read as "no AI yet".
      const response = await this.props.getActiveFeatures();
      if (!response?.data || this.unmounted || this.props.modalType.type) return;
      // Once an org has had either flag, even if an admin later turned it off,
      // the invitation is spent. A dunning-suspended org cannot enable it.
      const { features, ai_on_uploads_ever_enabled: everEnabled, features_suspended: suspended } = response.data;
      if (everEnabled !== false || suspended !== false || orgHasAiOnUploads(features)) return;

      // Manual AI still requires Billing V2 server side; legacy orgs get the automatic choice only.
      this.props.openModal({
        type: 'AI_ON_UPLOADS_PROMPT',
        data: { manualAvailable: response.data.billing_v2_routed === true, uid },
      });
      captureAnalytics('ai_on_uploads_prompt_shown');
    } catch {
      // A prompt is never worth breaking Home over.
    }
  };

  componentDidMount = async () => {
    window.addEventListener('resize', this.handleCarouselScroll);

    if (isOnChrome()) {
      this.showChromeExtensionInformation();
    }
    if (!this.state.userEMR) {
      await this.props.getUserInfo();
      this.setState({ userEMR: this.props.emr });
    }
    const {
      accessedResetPassword,
      accessedPatient,
      hasCompletedWalkthrough,
      openModal: openDialog,
    } = this.props;

    if (accessedResetPassword || accessedPatient) {
      const modalType = {
        type: 'LOGOUT_USER',
      };
      openDialog(modalType);
    } else if (
      hasCompletedWalkthrough
      && !this.props.impersonating
      && shouldShowMacPrinterDriverAnnouncement()
      // The modal is app-wide: if the user left Home during the awaits above,
      // opening it now would land on another page or replace its dialog.
      && !this.unmounted
      // Read the current modal after getUserInfo: a mandatory selector
      // or another dialog may have opened while it was pending.
      && !this.props.modalType.type
    ) {
      // Users still in the walkthrough are redirected to it, and its Tools step
      // already offers the driver.
      markMacPrinterDriverAnnouncementShown();
      openDialog({ type: 'MAC_PRINTER_DRIVER_ANNOUNCEMENT' });
      captureAnalytics('mac_printer_driver_announcement_shown');
    }

    // Not awaited: Home's own access checks below should not wait on the prompt.
    this.maybeShowAiOnUploadsPrompt();

    const { location } = this.props;
    const params = parse(location.search, { ignoreQueryPrefix: true });

    if (params.providerSwitched === 'true' && !hasCompletedWalkthrough && !this.props.impersonating) {
      const { history } = this.props;
      history.replace('./');
      this.openProviderSwitchedDialog();
    }

    const canViewVoiceAi = await verifyFeatureAccess('voice_ai');
    const hasUserActivity = await verifyFeatureAccess(USER_ACTIVITY_ANALYTICS_FEATURE);
    const canAccessAiChat = await verifyFeatureAccess('medsender_gpt') && window.innerWidth >= 800;
    const canAccessReceivedInbox = await verifyReceivedInboxAccess();
    const canAccessSentInbox = await verifySentInboxAccess();
    // Patient Recapture is sourced from the backend-derived m8_patient_recapture
    // localStorage key — User#patient_recapture short-circuits true for admins.
    const canAccessPatientRecapture = await verifyPatientRecaptureAccess();
    // Patient Engagement access is role-based — backend-derived
    // m8_patient_engagement key (User#patient_engagement).
    const canAccessPatientEngagement = await verifyPatientEngagementAccess();
    this.setState({
      canViewVoiceAi,
      hasUserActivity,
      canAccessAiChat,
      canAccessReceivedInbox,
      canAccessSentInbox,
      canAccessPatientRecapture,
      canAccessPatientEngagement,
    });

    axios.get('/api/v1/product_updates')
      .then(({ data }) => this.setState({ productUpdates: data, productUpdatesLoading: false }))
      .catch(() => this.setState({ productUpdatesLoading: false }));

  };

  openProviderSwitchedDialog = () => {
    const { openModal: openDialog } = this.props;
    const modalType = {
      type: 'PROVIDER_SWITCHED',
    };
    openDialog(modalType);
  };

  handleClose = () => {
    this.props.resetPatientAccessAttempt();
  };

  // Handler for the Upload Record button. Shows the modal to upload new record.
  openUploadModal = async () => {
    const canUpload = await verifyFeatureAccess('upload_record');
    const modal = canUpload ? 'UPLOAD_RECORD' : 'RAISE_FLAG';
    const modalType = {
      type: modal,
      ...(!canUpload && { featureFlag: 'upload_record' }),
    };

    this.props.openModal(modalType);
  };

  handleChangeDefaultOrganization = async (org) => {
     
    const {
      changeDefaultOrganization: handleChangeDefaultOrganization,
    } = this.props;
    this.setState({
      currentOrganization: org,
    });
    await handleChangeDefaultOrganization(org, this.reloadPage);
  };

  reloadPage = async () => {
    const {
      getLoginInfo: handleGetLoginInfo,
    } = this.props;
    await handleGetLoginInfo();
    this.context.router.history.push('/app');
  };

  openWebinarLink = () => {
    const win = window.open('https://meetings.hubspot.com/zain3/15-minute-demo', '_blank');
    win.opener = null;
  };

  openKareoLink = () => {
    const win = window.open('https://link.medsender.com/auto-upload-to-kareo');
    win.opener = null;
  };

  handleChromeExtension = () => {
    const win = window.open('https://chrome.google.com/webstore/detail/medsender/onpcajmldffklnfccjolcccoijaogggl');
    win.opener = null;
  };

  handleReferFriend = () => {
    if (this.props.impersonating) return;
    const { user } = this.props;
    const openGrowSurf = () => {
      if (user?.uid) {
        // Call addParticipant immediately before open for in-memory registration
        // This works even in Safari since we're not relying on persistent storage
        try {
          const parsedName = parseFullName(user?.fullName);
          const firstName = parsedName.first;
          const lastName = parsedName.last;
          window.growsurf.addParticipant({
            email: user?.uid,
            firstName,
            lastName,
          });
        } catch (error) {
          console.error('Error adding participant:', error);
        }
        window.growsurf.open();
      } else {
        window.growsurf.open();
      }
    };

    // Check if GrowSurf is already loaded
    if (window.growsurf && typeof window.growsurf.open === 'function') {
      openGrowSurf();
    } else {
      // Wait for GrowSurf to load
      let handled = false;

      const grsfReadyHandler = () => {
        if (handled) return;
        handled = true;
        clearTimeout(timeout);
        window.removeEventListener('grsfReady', grsfReadyHandler);
        openGrowSurf();
      };

      const timeout = setTimeout(() => {
        if (handled) return;
        handled = true;
        window.removeEventListener('grsfReady', grsfReadyHandler);
        if (window.growsurf && typeof window.growsurf.open === 'function') {
          openGrowSurf();
        } else {
          alert('Referral program is temporarily unavailable. Please try again later.');
        }
      }, 5000);

      window.addEventListener('grsfReady', grsfReadyHandler, { once: true });
    }
  };

  render() {
    const {
      openOrganizationFilterMenu,
      currentOrganization,
      chromeExtensionInstalled,
      canViewVoiceAi,
      hasUserActivity,
      canAccessAiChat,
      canAccessReceivedInbox,
      canAccessSentInbox,
      productUpdates,
      productUpdatesLoading,
      canAccessPatientRecapture,
      canAccessPatientEngagement,
    } = this.state;

    const {
      isAdmin,
      organizationDetails: { organizations },
      openModal: openDialog,
      referralManagement: canViewReferralMgmt,
      viewReferralAnalytics: canViewAnalytics,
      accessVoiceInbox,
    } = this.props;
    const canViewReferralAnalytics = (isAdmin && canViewReferralMgmt) || canViewAnalytics;
    const canViewMairaAnalytics = isAdmin && canViewVoiceAi;
    const canViewUserActivity = isAdmin && hasUserActivity;
    const kareoUser = this.state.userEMR === 'Kareo';
    const showBanner = window.innerWidth >= 1096;

    const referCopy = 'Refer & Earn';
    const referDescription = 'Refer a practice to Medsender and earn up to $250 cash while helping them reclaim hours back every week.';

    const iconSize = 22;
    const iconWeight = "duotone";

    return (
      <div className="dashboard">
        <Helmet>
          <title>Home - Medsender</title>
        </Helmet>
        <div style={{
          display: 'flex',
          flexDirection: showBanner ? 'row' : 'column',
          gap: '24px',
          alignItems: 'stretch',
          margin: '32px 0 0 0',
        }}>
          <Intro />
          {showBanner && (
            <Banner
              title={referCopy}
              size="small"
              description={referDescription}
              icon={<GiftFilled style={{ fontSize: '42px', color: COLORS.TEXT_SECONDARY_LITE }} />}
              onClick={this.handleReferFriend}
            />
          )}
        </div>

        <UpsellFlash
          products={[
            isAdmin
              && hasFeatureInLocalStorage('recapture_upsell_flag')
              && !hasFeatureInLocalStorage('patient_recapture')
              && 'recapture',
            isAdmin
              && hasFeatureInLocalStorage('arm_upsell_flag')
              && !hasFeatureInLocalStorage('arm_enabled')
              && 'arm',
            isAdmin
              && hasFeatureInLocalStorage('patient_engagement_upsell_flag')
              && !hasFeatureInLocalStorage('patient_engagement')
              && 'patientEngagement',
          ].filter(Boolean)}
        />

        {!chromeExtensionInstalled && (
          <Alert
            style={{ margin: '24px 0 0', cursor: 'pointer' }}
            message={(
              <div>
                <span style={{ fontWeight: 500 }}>{chromeExtensionNotInstalled}</span>
                &nbsp;
                {installChromeExtension}
              </div>
            )}
            onClick={this.handleChromeExtension}
            type="warning"
            showIcon
          />
        )}

        <div className="dashboard-grid">
          <DashboardButton
            to="/app/uploads"
            icon={<CloudArrowUp size={iconSize} weight={iconWeight} />}
            text="Uploads"
            description="Upload and manage documents"
            colorClass="dashboard-card-icon--blue"
          />
          {canAccessSentInbox && (
            <DashboardButton
              to="/app/sent"
              icon={<PaperPlaneTilt size={iconSize} weight={iconWeight} />}
              text="Sent"
              description="View sent faxes and messages"
              colorClass="dashboard-card-icon--orange"
            />
          )}
          {canAccessReceivedInbox && (
            <DashboardButton
              to="/app/received"
              icon={<EnvelopeSimple size={iconSize} weight={iconWeight} />}
              text="Inbox"
              description="Incoming faxes and documents"
              colorClass="dashboard-card-icon--purple"
            />
          )}
          <DashboardButton
            to="/app/assigned"
            icon={<ListChecks size={iconSize} weight={iconWeight} />}
            text="Assigned"
            description="Records assigned to you"
            colorClass="dashboard-card-icon--green"
          />
          {canViewReferralMgmt && (
            <DashboardButton
              to="/app/referred"
              icon={<Users size={iconSize} weight={iconWeight} />}
              text="Referral Management"
              description="Track and manage patient referrals"
              colorClass="dashboard-card-icon--pink"
            />
          )}
          {accessVoiceInbox && (
            <DashboardButton
              to="/app/voice_inbox"
              icon={<Phone size={iconSize} weight={iconWeight} />}
              text="Voice"
              description="Calls and voice recordings"
              colorClass="dashboard-card-icon--green"
            />
          )}
          {canAccessAiChat && (
            <DashboardButton
              onClick={() => this.props.setAiChatVisible(true)}
              icon={<Sparkle size={iconSize} weight={iconWeight} />}
              text="MedsenderGPT"
              description="Ask questions about your documents"
              colorClass="dashboard-card-icon--purple"
            />
          )}
          {(canViewReferralAnalytics || canViewMairaAnalytics || canViewUserActivity) && (
            <DashboardButton
              to="/app/analytics"
              icon={<ChartLine size={iconSize} weight={iconWeight} />}
              text="Analytics"
              description="Reports and performance insights"
              colorClass="dashboard-card-icon--indigo"
            />
          )}
          {canAccessPatientRecapture && (
            <DashboardButton
              to="/app/patient-recapture"
              icon={<UserSwitch size={iconSize} weight={iconWeight} />}
              text="Recapture"
              description="AI voice and SMS to bring overdue patients back"
              colorClass="dashboard-card-icon--pink"
            />
          )}
          {canAccessPatientEngagement && (
            <DashboardButton
              to="/app/patient-engagement"
              onClick={() => captureAnalytics('patient_engagement_home_tile_clicked')}
              icon={<ChatsCircle size={iconSize} weight={iconWeight} />}
              text="Patient Engagement"
              description="SMS reminders, confirmations, NPS, broadcasts"
              colorClass="dashboard-card-icon--blue"
            />
          )}
          <DashboardButton
            to="/app/contacts"
            icon={<AddressBook size={iconSize} weight={iconWeight} />}
            text="Contacts"
            description="Manage your address book"
            colorClass="dashboard-card-icon--teal"
          />
          <DashboardButton
            to="/app/user"
            icon={<GearSix size={iconSize} weight={iconWeight} />}
            text="Settings"
            description="Account and preferences"
            colorClass="dashboard-card-icon--slate"
          />
          <DashboardButton
            to="/app/guide"
            icon={<Question size={iconSize} weight={iconWeight} />}
            text="Help & Support"
            description="Guides and resources"
            colorClass="dashboard-card-icon--blue"
          />
          {!showBanner && (
            <DashboardButton
              onClick={this.handleReferFriend}
              icon={<Gift size={iconSize} weight={iconWeight} />}
              text={referCopy}
              description="Earn up to $250 per referral"
              colorClass="dashboard-card-icon--orange"
              badgeText="NEW"
            />
          )}
        </div>

        {(productUpdatesLoading || productUpdates.length > 0) && (
          <div className="dashboard-updates" ref={this.updatesRef}>
            <div className="dashboard-updates-header">
              <span className="dashboard-updates-title">What's New</span>
              <span className="dashboard-updates-subtitle">Latest product updates from the team</span>
            </div>
            <div
              className="dashboard-updates-carousel"
              ref={this.carouselRef}
              onMouseDown={this.handleCarouselMouseDown}
              onMouseMove={this.handleCarouselMouseMove}
              onMouseUp={this.handleCarouselMouseUp}
              onMouseLeave={this.handleCarouselMouseLeave}
              onScroll={this.handleCarouselScroll}
            >
              {productUpdatesLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="dashboard-update-card dashboard-update-card--skeleton">
                    <div className="dashboard-update-card-image-wrapper" />
                    <div className="dashboard-update-card-body">
                      <Skeleton active title={false} paragraph={{ rows: 2, width: ['40%', '90%'] }} />
                    </div>
                  </div>
                ))
              ) : (
                productUpdates.map((post) => (
                  <a
                    key={post.url || post.title}
                    href={post.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="dashboard-update-card"
                    onClickCapture={this.handleCarouselClick}
                    onClick={() => {
                      if (process.env.NODE_ENV !== 'development' && posthog) {
                        posthog.capture('product_update_clicked', {
                          title: post.title,
                          url: post.url,
                          labels: post.labels,
                        });
                      }
                    }}
                  >
                    {post.image_url && (
                      <div className="dashboard-update-card-image-wrapper">
                        <img src={post.image_url} alt="" className="dashboard-update-card-image" />
                      </div>
                    )}
                    <div className="dashboard-update-card-body">
                      <div className="dashboard-update-card-labels">
                        {post.labels.map((label) => (
                          <span key={label} className="dashboard-update-card-label">{label}</span>
                        ))}
                      </div>
                      <span className="dashboard-update-card-title">{post.title}</span>
                      {post.time_ago && (
                        <span className="dashboard-update-card-time">{post.time_ago}</span>
                      )}
                    </div>
                  </a>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}

Home.defaultProps = {
  impersonating: false,
  accessedPatient: false,
  accessedResetPassword: false,
  currentOrganizationId: null,
  modalType: { type: null },
};

Home.contextTypes = {
  router: PropTypes.object.isRequired,
};

Home.propTypes = {
  hasCompletedWalkthrough: PropTypes.bool.isRequired,
  openModal: PropTypes.func.isRequired,
  modalType: PropTypes.shape({ type: PropTypes.string }),
  resetPatientAccessAttempt: PropTypes.func.isRequired,
  accessedPatient: PropTypes.bool,
  accessedResetPassword: PropTypes.bool,
  emr: PropTypes.string.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  getUserInfo: PropTypes.func.isRequired,
  getActiveFeatures: PropTypes.func.isRequired,
  impersonating: PropTypes.bool,
  organizationDetails: PropTypes.shape({
    organizations: PropTypes.array.isRequired,
    current_organization: PropTypes.string,
  }).isRequired,
  changeDefaultOrganization: PropTypes.func.isRequired,
  getLoginInfo: PropTypes.func.isRequired,
  newReferralsCount: PropTypes.number.isRequired,
  notifications: PropTypes.array.isRequired,
  getNotifications: PropTypes.func.isRequired,
  referralManagement: PropTypes.bool.isRequired,
  viewReferralAnalytics: PropTypes.bool.isRequired,
  accessVoiceInbox: PropTypes.bool.isRequired,
  user: PropTypes.object.isRequired,
  currentOrganizationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default connect((state) => ({
  modalType: state.modal.modalType,
  hasCompletedWalkthrough: state.auth.hasCompletedWalkthrough,
  accessedPatient: state.auth.accessedPatient,
  accessedResetPassword: state.auth.accessedResetPassword,
  emr: state.auth.data.emr,
  jobTitle: state.auth.data.jobTitle,
  isAdmin: state.auth.isAdmin,
  organizationDetails: state.auth.organizationDetails,
  newReferralsCount: state.socket.newReferralsCount,
  notifications: state.notifications.notifications,
  referralManagement: state.auth.referralManagement,
  viewReferralAnalytics: state.auth.viewReferralAnalytics,
  accessVoiceInbox: state.auth.accessVoiceInbox,
  user: state.auth.data,
  currentOrganizationId: state.auth.currentOrganizationId,
  impersonating: state.auth.impersonating,
}), {
  resetPatientAccessAttempt, openModal, getUserInfo, getActiveFeatures, changeDefaultOrganization, getLoginInfo, getNotifications, setAiChatVisible,
})(Home);

export {
  Home,
};
